import React, { memo, useCallback, useMemo } from 'react';

import { format } from 'd3';
import Decimal from 'decimal.js';

import { toBN } from '@utils/formaters.utils';

import Chart from '@pages/Vault/LiquidityChart/components/Chart';

import { FeeAmount } from '@constants/vaults/vault.constants';

import useStyles from './styles.module';
import { ChartEntry, ZOOM_LEVELS } from './types/types';

const LiquidityChart = ({
  feeAmount,
  price,
  minPercent,
  maxPercent,
  formattedData,
  onSetMinPercent,
  onSetMaxPercent,
  onResetState,
  interactive = true,
  isResetDisabled,
}: {
  price?: number;
  minPercent: Decimal;
  maxPercent: Decimal;
  formattedData: ChartEntry[] | undefined;
  feeAmount?: FeeAmount;
  interactive?: boolean;
  isResetDisabled: boolean;
  onSetMinPercent: (value: Decimal) => void;
  onSetMaxPercent: (value: Decimal) => void;
  onResetState: () => void;
}) => {
  const { classes } = useStyles();

  const brushDomain: [number, number] | undefined = useMemo(() => {
    const min = toBN(price).times(
      toBN(1).plus(toBN(minPercent.isNaN() ? 0 : Number(minPercent)).div(100)),
    );
    const max = toBN(price).times(
      toBN(1).plus(toBN(maxPercent.isNaN() ? 0 : Number(maxPercent)).div(100)),
    );

    return [min.toNumber(), max.toNumber()];
  }, [price, minPercent, maxPercent]);

  const onBrushDomainChangeEnded = useCallback(
    (domain: [number, number], mode?: string) => {
      if (mode !== 'user') {
        return;
      }

      if (!price) return;

      const [leftPrice, rightPrice] = domain;

      const toPercent = (val: number) =>
        (val < price ? -1 : 1) *
        ((Math.max(val, price) - Math.min(val, price)) / price) *
        100;

      const leftPercent = toBN(toPercent(leftPrice).toFixed(2));
      const rightPercent = toBN(toPercent(rightPrice).toFixed(2));

      onSetMinPercent(leftPercent);
      onSetMaxPercent(rightPercent);
    },
    [price, onSetMinPercent, onSetMaxPercent],
  );

  const brushLabelValue = useCallback(
    (d: 'w' | 'e', x: number) => {
      if (!price) return '';

      const percent =
        (x < price ? -1 : 1) *
        ((Math.max(x, price) - Math.min(x, price)) / price) *
        100;

      return price
        ? `${format(Math.abs(percent) > 1000 ? '.2s' : '.2f')(percent)}%`
        : '';
    },
    [price],
  );

  const zoomLevels = useMemo(
    () =>
      ZOOM_LEVELS[feeAmount || FeeAmount.MEDIUM] ||
      ZOOM_LEVELS[FeeAmount.MEDIUM],
    [feeAmount],
  );

  return (
    <div className={classes.box}>
      {formattedData && price && zoomLevels && (
        <Chart
          data={{
            series: formattedData,
            current: price,
          }}
          dimensions={{
            width: 600,
            height: 200,
          }}
          margins={{
            top: 10,
            right: 2,
            bottom: 20,
            left: 0,
          }}
          interactive={interactive && Boolean(formattedData?.length)}
          brushLabels={brushLabelValue}
          brushDomain={brushDomain}
          onBrushDomainChange={onBrushDomainChangeEnded}
          zoomLevels={zoomLevels}
          onResetState={onResetState}
          isResetDisabled={isResetDisabled}
          showZoomButtons={!!brushDomain}
        />
      )}
    </div>
  );
};

export default memo(LiquidityChart);
